import axios, {
 AxiosError,
 type AxiosRequestConfig,
 type InternalAxiosRequestConfig,
} from "axios";
import {
 clearSession,
 getRefreshToken,
 notifyForbidden,
 setTokens,
 TOKEN_KEY,
} from "./auth";
import { API_BASE_URL, apiUrl } from "./apiConfig";
import { notifyApiError } from "./notification.service";

const API = axios.create({
 baseURL: API_BASE_URL,
 withCredentials: true,
});

/**
 * Force cache refresh on next GET request.
 * Call this after any mutation (create/update/delete) to bypass server cache.
 */
let forceRefreshNextRequest = false;

export function invalidateCache(): void {
 forceRefreshNextRequest = true;
}

/**
 * Make a GET request that bypasses cache (adds cache-busting header).
 * Use this after mutations to ensure fresh data.
 */
export function getWithCacheBust<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T> {
 return API.get(url, {
 ...config,
 headers: {
 ...config?.headers,"Cache-Control":"no-cache","X-Force-Refresh":"true",
 },
 }).then((res) => res.data);
}

API.interceptors.request.use((config) => {
 if (typeof window !=="undefined") {
 const token = window.localStorage.getItem(TOKEN_KEY);
 if (token) {
 config.headers = config.headers ?? {};
 (config.headers as Record<string, string>).Authorization =`Bearer ${token}`;
 }
 }
 
 // Add cache-busting header if flagged
 if (forceRefreshNextRequest && config.method?.toLowerCase() ==="get") {
 config.headers = config.headers ?? {};
 (config.headers as Record<string, string>)["Cache-Control"] ="no-cache";
 (config.headers as Record<string, string>)["X-Force-Refresh"] ="true";
 forceRefreshNextRequest = false;
 }
 
 return config;
});

/* ----------------------------------------------------------------------------
 * Refresh-on-401 interceptor.
 *
 * When the server replies 401 to a normal API call, we try ONCE to
 * exchange the refresh token for a new access + refresh pair, then
 * replay the failed request transparently. If the refresh itself fails
 * we clear the local session and bounce the user to the login screen.
 *
 * Single-flight: if 12 requests fire in parallel and all come back
 * 401 (e.g. the access token just expired), only ONE refresh POST is
 * issued — the rest wait on the same promise and get retried.
 *
 *`/api/auth/refresh`and the two login endpoints are skipped so an
 * invalid refresh / bad credential doesn't trigger a refresh loop.
 * -------------------------------------------------------------------- */

type RetryableConfig = InternalAxiosRequestConfig & {
 _retriedAfterRefresh?: boolean;
};

const isAuthEndpoint = (url?: string): boolean => {
 if (!url) return false;
 return (
 url.includes("/api/auth/login") ||
 url.includes("/api/auth/admin/login") ||
 url.includes("/api/auth/refresh") ||
 url.includes("/api/auth/logout")
 );
};

interface RefreshResponse {
 data?: {
 token?: string;
 accessToken?: string;
 refreshToken?: string;
 };
}

let pendingRefresh: Promise<string | null> | null = null;

const refreshTokenPair = async (): Promise<string | null> => {
 const refresh = getRefreshToken();
 if (!refresh) return null;

 try {
 // Use the bare axios instance, NOT`API`, so the request interceptor
 // above doesn't attach the (likely-expired) bearer header.
 const res = await axios.post<RefreshResponse>(
 apiUrl("/api/auth/refresh"),
 { refreshToken: refresh },
 { withCredentials: true }
 );
 const data = res.data?.data;
 const nextToken = data?.accessToken ?? data?.token ?? null;
 const nextRefresh = data?.refreshToken ?? null;
 if (!nextToken) return null;
 setTokens(nextToken, nextRefresh);
 return nextToken;
 } catch {
 return null;
 }
};

API.interceptors.response.use(
 (response) => response,
 async (error: AxiosError<{ message?: string }>) => {
 const status = error?.response?.status;
 const original = error?.config as RetryableConfig | undefined;
 const requestUrl = original?.url;

 /* ---- 403 handling ----
 *
 * Two flavours of 403:
 * 1.`{ code:'PASSWORD_CHANGE_REQUIRED'}`— server says the
 * user must complete the OTP-verified password change flow
 * before any other action is allowed. We hard-redirect to
 *`/change-password`using a full-page navigation so that
 * in-flight React state (TanStack caches, form drafts, the
 * locked page itself) is torn down cleanly. We do NOT use
 * React Router hooks here — interceptors can fire from
 * outside the component tree, and importing the router
 * would also create a circular dep with this file.
 *
 * 2. Any other 403 — surface as a transient toast and reject
 * so the caller can branch on the error.
 * ----------------------------------------------------------------- */
 if (status === 403) {
 const apiMessage = error?.response?.data?.message;
 notifyForbidden(
 apiMessage ||"You don't have permission to perform that action.");
 return Promise.reject(error);
 }

 if (
 status !== 401 ||
 !original ||
 original._retriedAfterRefresh ||
 isAuthEndpoint(requestUrl)
 ) {
 // Either it wasn't a 401, we've already retried once, or the
 // failing request is itself an auth endpoint — in any of those
 // cases we wipe and bounce.
 if (status === 401 && !isAuthEndpoint(requestUrl)) {
 clearSession();
 if (typeof window !=="undefined"&& window.location.pathname !=="/") {
 window.location.replace("/");
 }
 }
 return Promise.reject(error);
 }

 /* ---- Single-flight refresh ---- */
 pendingRefresh = pendingRefresh ?? refreshTokenPair();
 const newToken = await pendingRefresh;
 pendingRefresh = null;

 if (!newToken) {
 clearSession();
 if (typeof window !=="undefined"&& window.location.pathname !=="/") {
 window.location.replace("/");
 }
 return Promise.reject(error);
 }

 // Replay with the fresh token.
 original._retriedAfterRefresh = true;
 original.headers = original.headers ?? {};
 (original.headers as Record<string, string>).Authorization =`Bearer ${newToken}`;
 return API.request(original as AxiosRequestConfig);
 }
);

/**
 * Auto-notification interceptor for API errors.
 * Fires a toast notification for server errors (5xx), network errors, and
 * validation errors — without interfering with existing 401/403 handling.
 */
API.interceptors.response.use(
 undefined,
 (error: AxiosError<{ message?: string }>) => {
 const status = error?.response?.status;
 const url = error?.config?.url ||"";

 // Skip auth endpoints (login, refresh, logout) — handled elsewhere
 if (
 url.includes("/api/auth/login") ||
 url.includes("/api/auth/refresh") ||
 url.includes("/api/auth/logout")
 ) {
 return Promise.reject(error);
 }

 // Skip 401/403 — already handled by the previous interceptor
 if (status === 401 || status === 403) {
 return Promise.reject(error);
 }

 // Notify on 5xx server errors
 if (status && status >= 500) {
 notifyApiError(error);
 }
 // Notify on network errors (no response at all)
 else if (!error.response && error.message) {
 notifyApiError(error,"Network Error");
 }
 // Notify on 429 rate limiting
 else if (status === 429) {
 notifyApiError(error,"Too Many Requests");
 }

 return Promise.reject(error);
 }
);

export default API;
